
import {Component, Vue} from 'vue-property-decorator';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipe from 'photoswipe';
import 'photoswipe/style.css';
import {MediaService} from '@/services/media.service';

interface ImageData {
  src: string;
  width: number;
  height: number;
  mediumId: number;
}

@Component
export default class PreviewGallery extends Vue {
  galleryId = 'preview-gallery';

  lightbox: PhotoSwipeLightbox | null = null;

  currentPswp: PhotoSwipe | null = null;

  currentUrls: string[] = [];

  imageCache: Map<number, ImageData> = new Map();

  isLoadingBackground = false;

  currentDataSource: Array<{src: string, width: number, height: number}> = [];

  mounted() {
    this.initializeLightbox();
  }

  unmounted() {
    this.cleanup();
  }

  beforeDestroy() {
    this.cleanup();
  }

  private initializeLightbox() {
    this.lightbox = new PhotoSwipeLightbox({
      gallerySelector: `#${this.galleryId}`,
      childSelector: 'a',
      pswpModule: () => import('photoswipe'),
    });

    this.lightbox.on('beforeOpen', () => {
      this.currentPswp = this.lightbox!.pswp || null;
    });

    this.lightbox.on('destroy', () => {
      this.revokeUrls();
      this.imageCache.clear();
      this.currentPswp = null;
      this.currentDataSource = [];
    });

    this.lightbox.init();
  }

  private cleanup() {
    if (this.lightbox) {
      this.lightbox.destroy();
      this.lightbox = null;
    }
    this.revokeUrls();
    this.imageCache.clear();
    this.currentPswp = null;
    this.currentDataSource = [];
  }

  private revokeUrls() {
    this.currentUrls.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        // Ignore cleanup errors
      }
    });
    this.currentUrls = [];
  }

  async open(mediumIds: number[], startId: number): Promise<void> {
    if (!mediumIds || mediumIds.length === 0) {
      return;
    }

    this.$loading.start();

    try {
      const startIndex = mediumIds.indexOf(startId);
      const actualStartIndex = startIndex >= 0 ? startIndex : 0;

      const priorityBatchSize = 5;
      const priorityIds = mediumIds.slice(actualStartIndex, actualStartIndex + priorityBatchSize);

      await this.fetchAndCachePriorityImages(priorityIds);

      if (this.imageCache.size === 0) {
        return;
      }

      const loadedImages = Array.from(this.imageCache.values());
      this.currentDataSource = loadedImages.map(img => ({
        src: img.src,
        width: img.width,
        height: img.height,
      }));

      const newStartIndex = loadedImages.findIndex(img => img.mediumId === startId);
      const finalStartIndex = newStartIndex >= 0 ? newStartIndex : 0;

      this.openGallery(this.currentDataSource, finalStartIndex);

      this.loadRemainingImagesInBackground(mediumIds, priorityIds);

    } catch (error) {
      this.$notifier.error([this.$t(this.$LF.UnableToLoadPreview) as string]);
      // console.error('PreviewGallery error:', error);
    } finally {
      this.$loading.stop();
    }
  }

  private async fetchAndCachePriorityImages(priorityIds: number[]): Promise<void> {
    const mediaService = new MediaService();

    for (const id of priorityIds) {
      try {
        const blob = await mediaService.downloadFile(id);

        if (blob && blob.type.startsWith('image/')) {
          const url = URL.createObjectURL(blob);
          this.currentUrls.push(url);

          const dimensions = await this.getImageDimensions(url);

          this.imageCache.set(id, {
            src: url,
            width: dimensions.width,
            height: dimensions.height,
            mediumId: id,
          });
        } else {
          // console.info(`Skipping non-image media ${id} (type: ${blob?.type || 'unknown'})`);
        }
      } catch (error) {
        // console.warn(`Failed to download priority media ${id}:`, error);
      }
    }
  }

  private async loadRemainingImagesInBackground(allIds: number[], priorityIds: number[]): Promise<void> {
    if (this.isLoadingBackground) return;
    this.isLoadingBackground = true;

    const remainingIds = allIds.filter(id => !priorityIds.includes(id));
    const mediaService = new MediaService();

    for (const id of remainingIds) {
      if (this.imageCache.has(id)) continue;

      try {
        const blob = await mediaService.downloadFile(id);

        if (blob && blob.type.startsWith('image/')) {
          const url = URL.createObjectURL(blob);
          this.currentUrls.push(url);

          const dimensions = await this.getImageDimensions(url);

          const imageData: ImageData = {
            src: url,
            width: dimensions.width,
            height: dimensions.height,
            mediumId: id,
          };

          this.imageCache.set(id, imageData);

          if (this.currentDataSource && this.currentDataSource.length > 0) {
            this.currentDataSource.push({
              src: imageData.src,
              width: imageData.width,
              height: imageData.height,
            });
          }
        } else {
          // console.info(`Skipping non-image media ${id} (type: ${blob?.type || 'unknown'})`);
        }
      } catch (error) {
        // console.warn(`Failed to download media ${id} in background:`, error);
      }
    }

    this.isLoadingBackground = false;
  }

  private getImageDimensions(url: string): Promise<{width: number, height: number}> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  private openGallery(dataSource: Array<{src: string, width: number, height: number}>, startIndex: number) {
    if (!this.lightbox) {
      this.initializeLightbox();
    }

    this.lightbox!.loadAndOpen(startIndex, dataSource);
  }
}
